import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
} from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { addDays, format } from 'date-fns';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  combineLatest,
  debounceTime,
  filter,
  map,
  share,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import { NewFlightLogDialogComponent } from 'src/app/flight-logs/components/new-flight-log-dialog/new-flight-log-dialog.component';
import { FlightLogSummaryTableSort } from 'src/app/shared/components/flight-log-summaries-table/flight-log-summaries-table.component';
import {
  FlightLogSummarySortableField,
  isValidDateRange,
  Sort,
  tryGetSort,
  tryGetSortOrDefault,
  tryParseDateOrDefault,
} from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogHttpClient } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.service';
import { sleep } from 'src/util/async';
import { distinctUntilObjectChanged } from 'src/util/rxjs/operators';
import {
  getPageIndex,
  getPageQueryParams,
  getPageSize,
  tryGetSkipTakeOrDefault,
} from 'src/util/ui/pagination';
import { withoutNullOrEmptyValues } from 'src/util/values';
import { UserViewModel } from 'src/view-models/user-view-model';
import { FlightLogHighlightsViewModel } from '../../../../view-models/flight-log-highlights-view-model';
import {
  FlightLogHighlightsTableColumn,
  FlightLogHighlightsTableComponent,
} from '../../../shared/components/flight-log-highlights-table/flight-log-highlights-table.component';

interface RouteParams {
  startDate?: string;
  endDate?: string;
  deleted?: string;
  dispatchId?: string;
  tailNumber?: string;
  flightTagNumber?: string;
  employeeId?: string;
  orderBy?: FlightLogSummarySortableField;
  direction?: 'asc' | 'desc';
  page?: string;
  pageSize?: string;
}

interface FlightLogIndexParams {
  startTime?: Date;
  endTime?: Date;
  dispatchId?: string;
  tailNumber?: string;
  deleted: boolean;
  flightTagNumber?: string;
  employeeId?: string;
  skip: number;
  take: number;
  myFlightLogsOnly: boolean;
  sort?: Sort<FlightLogSummarySortableField>;
}

function parseQueryParams(
  queryParams: RouteParams,
  defaultPageSize: number,
  defaultSort: Sort<FlightLogSummarySortableField>,
  defaultStartDate: Date,
  defaultEndDate: Date,
  user: UserViewModel,
): FlightLogIndexParams {
  return {
    myFlightLogsOnly: user.isAviationSupervisor && queryParams.employeeId === user.id,
    startTime: tryParseDateOrDefault(queryParams.startDate, defaultStartDate),
    endTime: tryParseDateOrDefault(queryParams.endDate, defaultEndDate),
    employeeId: queryParams.employeeId,
    tailNumber: queryParams.tailNumber,
    flightTagNumber: queryParams.flightTagNumber,
    dispatchId: queryParams.dispatchId,
    deleted: queryParams.deleted === 'true',
    sort: tryGetSortOrDefault(queryParams, defaultSort),
    ...tryGetSkipTakeOrDefault(queryParams, defaultPageSize),
  };
}

function serializeFormValues(formParams: FlightLogIndexParams): RouteParams {
  const routeParams: RouteParams = {
    startDate:
      formParams.startTime != null ? format(formParams.startTime, 'YYYY-MM-DD') : undefined,
    endDate: formParams.endTime != null ? format(formParams.endTime, 'YYYY-MM-DD') : undefined,

    tailNumber: formParams.tailNumber,
    dispatchId: formParams.dispatchId,
    deleted: formParams.deleted === true ? 'true' : undefined,
    employeeId: formParams.employeeId,
    flightTagNumber: formParams.flightTagNumber,
  };

  return withoutNullOrEmptyValues(routeParams);
}

@Component({
  selector: 'fl-flight-log-index-page',
  templateUrl: './flight-log-index-page.component.html',
  styleUrls: ['./flight-log-index-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightLogIndexPageComponent implements OnInit, OnDestroy {
  get features() {
    return this._user.flightLogFeatures;
  }
  get isAviationSupervisor() {
    return this._user.isAviationSupervisor;
  }

  @ViewChild(FlightLogHighlightsTableComponent, { static: true })
  public table: FlightLogHighlightsTableComponent;

  public flightLogDeleted$ = new BehaviorSubject(null);
  public flightLogRestored$ = new BehaviorSubject(null);

  public reportParameters: UntypedFormGroup;
  public flightLogs$: Observable<FlightLogHighlightsViewModel[]>;

  public _pageRefresh$ = new BehaviorSubject<{}>({});

  public defaultPageSize = 15;
  public pageSizeOptions = [10, this.defaultPageSize, 25, 50, 100];
  public defaultSort: Sort<FlightLogSummarySortableField> = {
    orderBy: 'flightTime',
    direction: 'desc',
  };
  public sort$ = new Subject<FlightLogSummaryTableSort>();
  public _onDestroy$ = new Subject<void>();

  public page$ = new Subject<PageEvent>();
  public pageIndex$: Observable<number>;
  public pageSize$: Observable<number | undefined>;
  public total$: Observable<number>;
  public isValidDateRange$: Observable<boolean>;

  public displayMyFlightsCtrl: UntypedFormControl = new UntypedFormControl();
  public maxNoOfDays = 60;

  get deletedCtrl(): UntypedFormControl {
    return this.reportParameters.get('deleted') as UntypedFormControl;
  }

  get employeeIdCtrl() {
    return this.reportParameters.get('employeeId') as UntypedFormControl;
  }

  public columns: FlightLogHighlightsTableColumn[] = [
    'flightTime',
    'flightType',
    'flightTagNumber',
    'flightSummaryId',
    'pilotInCommand',
    'base',
    'tailNumber',
    'status',
    'flightStatus',
    'actions',
  ];

  constructor(
    @Inject(USER) private readonly _user: UserViewModel,
    private _route: ActivatedRoute,
    private _router: Router,
    private _fb: UntypedFormBuilder,
    private _flightLogs: FlightLogHttpClient,
    public _dialog: MatDialog,
  ) {
    const defaultEndDate = new Date();
    const defaultStartDate = addDays(defaultEndDate, -this.maxNoOfDays);

    const params$ = this._route.queryParams.pipe(
      map((params) =>
        parseQueryParams(
          params,
          this.defaultPageSize,
          this.defaultSort,
          defaultStartDate,
          defaultEndDate,
          this._user,
        ),
      ),
      distinctUntilObjectChanged(),
    );

    this.isValidDateRange$ = params$.pipe(
      map((params) => isValidDateRange(params.startTime, params.endTime, this.maxNoOfDays)),
    );

    const flightLogResults = params$.pipe(
      // eslint-disable-next-line import/no-deprecated
      combineLatest(this._pageRefresh$),
      map(([params]) => {
        this.reportParameters.patchValue(
          {
            startTime: params.startTime,
            endTime: params.endTime,
            dispatchId: params.dispatchId,
            tailNumber: params.tailNumber,
            flightTagNumber: params.flightTagNumber,
            employeeId: params.employeeId,
            deleted: params.deleted,
          },
          { emitEvent: false },
        );
        return params;
      }),
      filter((params) => isValidDateRange(params.startTime, params.endTime, this.maxNoOfDays)),
      switchMap((params) => {
        this.table.markAsLoading();
        this.displayMyFlightsCtrl.setValue(params.myFlightLogsOnly, {
          emitEvent: false,
        });
        const employeeId = this._user.isAviationSupervisor ? params.employeeId : this._user.id;
        return this._flightLogs.getManyWithHighlights({
          employeeId,
          startTime: params.startTime,
          endTime: params.endTime,
          // We want the pilots to see any flight they were on as a flight crew member
          // no matter which company they flew for.
          companyId: employeeId != null ? undefined : this._user.companycode,
          dispatchId: params.dispatchId,
          flightTagNumber: params.flightTagNumber,
          tailNumber: params.tailNumber,
          deletedOnly: params.deleted === true,
          limit: params.take,
          skip: params.skip,
          sort: params.sort,
        });
      }),
      share(),
    );

    this.flightLogs$ = flightLogResults.pipe(
      map((fl) => {
        this.table.markAsLoaded();
        return fl.results;
      }),
    );
    this.total$ = flightLogResults.pipe(map((fl) => fl.totalCount));
  }

  public ngOnInit() {
    this.table.markAsLoading();

    this.reportParameters = this._fb.group({
      startTime: null,
      endTime: null,
      dispatchId: null,
      tailNumber: null,
      flightTagNumber: null,
      employeeId: null,
      deleted: null,
    });

    this.reportParameters.valueChanges
      .pipe(debounceTime(1000), takeUntil(this._onDestroy$))
      .subscribe((value: FlightLogIndexParams) => {
        const params = serializeFormValues(value);
        this._router.navigate(['flight-logs'], { queryParams: params });
      });

    this.pageIndex$ = getPageIndex(this._route);
    this.pageSize$ = getPageSize(this._route, this.defaultPageSize);
    this.page$.pipe(takeUntil(this._onDestroy$)).subscribe((page) => {
      this.patchQueryParams(getPageQueryParams(page, this.defaultPageSize));
    });
    this.sort$.pipe(takeUntil(this._onDestroy$)).subscribe((sort) => {
      const orderBy = tryGetSort(sort);
      this.patchQueryParams(orderBy != null ? orderBy : { orderBy: null, direction: null });
    });
    this.displayMyFlightsCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((myFlights) => {
        this.employeeIdCtrl.setValue(myFlights != null ? this._user.id : null);
      });
  }

  public async refresh(delay?: number) {
    this.table.markAsLoading();
    if (delay != null) {
      await sleep(delay);
    }
    this._pageRefresh$.next({});
  }

  public onPage(page: PageEvent) {
    this.page$.next(page);
  }

  public onSort(sort: FlightLogSummaryTableSort) {
    this.sort$.next(sort);
  }

  public patchQueryParams<T extends {}>(params: T | undefined) {
    params = params != null ? params : ({} as any);
    const currentQueryparams = this._route.snapshot.queryParams;
    const queryParams = {
      ...currentQueryparams,
      ...params,
    };
    this._router.navigate(['flight-logs'], { queryParams });
  }

  public viewFlight(flightLogId: string) {
    this._router.navigateByUrl(`/flight-logs/${flightLogId}`);
  }

  public openNewFlightLogDialog() {
    const config: MatDialogConfig = {
      disableClose: true,
    };
    this._dialog.open(NewFlightLogDialogComponent, config);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }
}
